/**
 * Spot exclusivity engagement slice (04/06/2026).
 *
 * Owns:
 *   • `spotEngagements`     — active engagements with the client (90-day window)
 *   • Spot-call lifecycle   — randomised generation, deadline tracking,
 *                              expiry & strike system
 *
 * Public API (called by other slices):
 *   • `startSpotEngagement(tender)`      — when a spot tender is won
 *   • `tickSpotEngagements()`            — called every day from coreSlice.nextDay
 *   • `acceptSpotCall(callId, ...)`      — same signature as regular contract acceptance
 *   • `getActiveSpotEngagements()`       — selector for UI
 *
 * State shape:
 *   spotEngagements: [{
 *     id, clientName, tier, sector,
 *     startedOnDay, endsOnDay,
 *     callsPerMonthMin, callsPerMonthMax,
 *     callPayoutRange,
 *     responseDeadlineDays,
 *     strikes: 0,                     // 3 strikes = termination
 *     plannedCallDays: [12, 17, 23],  // pre-rolled schedule for the engagement
 *     lastCallSpawnDay: null,
 *   }]
 *
 * Spot calls live in `availableContracts` like any other contract but
 * carry the marker `spotCallEngagementId` so the UI shows them in purple
 * and the deadline countdown.
 */
import { generateId } from '../helpers';

/**
 * Pre-roll the call schedule for an engagement.
 * We want: roughly callsPerMonthMin..Max calls per 30-day window over
 * engagementDays. We spread them randomly across each 30-day month with a
 * minimum 2-day gap so the player isn't overwhelmed.
 */
function rollEngagementSchedule(startDay, engagementDays, minPerMonth, maxPerMonth, rng = Math.random) {
  const days = [];
  const months = Math.ceil(engagementDays / 30);
  for (let m = 0; m < months; m++) {
    const count = minPerMonth + Math.floor(rng() * (maxPerMonth - minPerMonth + 1));
    const monthStart = startDay + m * 30;
    const monthEnd = Math.min(startDay + engagementDays, monthStart + 30);
    const monthSpan = monthEnd - monthStart;
    if (monthSpan <= 0) break;
    const monthDays = new Set();
    let safety = 0;
    while (monthDays.size < count && safety < 50) {
      const offset = Math.floor(rng() * monthSpan);
      const day = monthStart + offset;
      // Enforce min 2-day gap.
      let ok = true;
      for (const d of monthDays) {
        if (Math.abs(d - day) < 2) { ok = false; break; }
      }
      if (ok) monthDays.add(day);
      safety += 1;
    }
    days.push(...monthDays);
  }
  return days.sort((a, b) => a - b);
}

export function createSpotEngagementSlice(set, get) {
  return {
    spotEngagements: [],

    /**
     * Called when a spot-typed tender is won.
     * @param {Object} tender  — entry from SPOT_TENDERS
     */
    startSpotEngagement: (tender) => {
      const state = get();
      const startDay = state.totalDays || 0;
      const engagement = {
        id: `spot-engagement-${tender.id}-${generateId()}`,
        tenderId: tender.id,
        clientName: tender.clientName,
        sector: tender.sector,
        tier: tender.tier,
        startedOnDay: startDay,
        endsOnDay: startDay + (tender.engagementDays || 90),
        callsPerMonthMin: tender.callsPerMonthMin || 3,
        callsPerMonthMax: tender.callsPerMonthMax || 5,
        callPayoutRange: tender.callPayoutRange || [1800, 2400],
        responseDeadlineDays: tender.responseDeadlineDays || 3,
        strikes: 0,
        plannedCallDays: rollEngagementSchedule(
          startDay + 2, // first call earliest 2 days later
          tender.engagementDays || 90,
          tender.callsPerMonthMin || 3,
          tender.callsPerMonthMax || 5,
        ),
        completedCalls: 0,
        missedCalls: 0,
        active: true,
      };
      set({ spotEngagements: [...(state.spotEngagements || []), engagement] });
      // Welcome message in inbox.
      if (typeof get().pushCustomMessage === 'function') {
        try {
          get().pushCustomMessage({
            senderType: 'client',
            senderName: tender.clientName,
            tone: 'positive',
            kind: 'positive',
            subject: `Exclusivité ponctuelle confirmée — ${tender.clientName}`,
            body:
              `Bonjour,\n\nNous confirmons votre référencement comme transporteur exclusif pour nos demandes ponctuelles imprévues sur les 90 prochains jours.\n\n` +
              `📞 **${tender.callsPerMonthMin}-${tender.callsPerMonthMax} missions par mois** attendues, à honorer sous **${tender.responseDeadlineDays} jours**.\n\n` +
              `Premier appel imminent. Tenez véhicule + chauffeur prêts.\n\nCordialement,\nL'équipe ${tender.clientName}`,
            ttlDays: 14,
          });
        } catch (err) { console.warn('[spotEngagement] welcome message failed:', err); }
      }
      return engagement;
    },

    /**
     * Daily tick: spawn calls due today + expire missed calls + close
     * engagements past their endsOnDay.
     */
    tickSpotEngagements: () => {
      const state = get();
      const today = state.totalDays || 0;
      const engagements = state.spotEngagements || [];
      if (engagements.length === 0) return;

      let avail = [...(state.availableContracts || [])];
      const messages = [];
      let reputationDelta = 0;
      let cashDelta = 0;
      let updatedEngagements = engagements.map(eng => ({ ...eng }));
      const toEnd = []; // engagement ids to flag as terminated

      // 1. Spawn calls scheduled for today.
      // 10/02/2026 (Lot J Bug 2) — Retour user prod : « Je me suis pris
      // une pénalité alors que le contrat n'est pas apparu ». Le spot
      // call était ajouté silencieusement à `availableContracts` sans
      // notification visible. Conséquence : si le user était sur une
      // autre page, il loupait la fenêtre d'acceptation et subissait
      // les -800 €/-3 rep à l'expiration. On pousse désormais un message
      // inbox dédié + une notification urgente pour chaque spot call.
      const newCallMessages = [];
      const newCallNotifications = [];
      updatedEngagements.forEach(eng => {
        if (!eng.active) return;
        const dueToday = (eng.plannedCallDays || []).filter(d => d === today);
        dueToday.forEach(() => {
          const [low, high] = eng.callPayoutRange || [1800, 2400];
          const revenue = Math.round(low + Math.random() * (high - low));
          const deadlineDay = today + (eng.responseDeadlineDays || 3);
          const callContract = {
            instanceId: `spot-call-${eng.id}-${today}-${generateId().slice(0, 6)}`,
            id: `spot-call-${eng.tenderId}`,
            name: `${eng.clientName} — Appel exclusif`,
            clientName: eng.clientName,
            client: eng.clientName,
            category: eng.sector,
            frequency: 'ponctuel',
            duration: 1,
            revenue,
            dailyRevenue: revenue,
            tier: 1,
            tierMin: 1,
            // Spot call markers (UI uses these for purple styling)
            spotCall: true,
            spotCallEngagementId: eng.id,
            spotCallDeadlineDay: deadlineDay,
            spawnDay: today,
            // Standard requirements (no specialty)
            requiredLicenses: ['C'],
          };
          avail.push(callContract);
          // Inbox alert so the user actually SEES the call.
          newCallMessages.push({
            senderType: 'client',
            senderName: eng.clientName,
            tone: 'urgent',
            kind: 'warning',
            subject: `🔔 Appel ponctuel — ${eng.clientName} (${revenue.toLocaleString('fr-FR')} €)`,
            body:
              `Bonjour,\n\nNous avons besoin d'un transport en urgence : « ${callContract.name} » pour un montant de **${revenue.toLocaleString('fr-FR')} €**.\n\n` +
              `⏰ **Délai d'acceptation** : J${deadlineDay} (${(eng.responseDeadlineDays || 3)} jours).\n` +
              `⚠️ En cas de non-réponse : pénalité 800 € + -3 réputation et un strike sur votre référencement.\n\n` +
              `Allez sur la page « Appels d'offres / Contrats disponibles » pour accepter cet appel ponctuel.\n\nCordialement,\nL'équipe ${eng.clientName}`,
            ttlDays: (eng.responseDeadlineDays || 3) + 2,
            payload: { spotCall: true, contractInstanceId: callContract.instanceId },
          });
          newCallNotifications.push({
            id: generateId(),
            type: 'spot_call_incoming',
            title: `Appel ponctuel — ${eng.clientName}`,
            message: `Nouvelle mission urgente ${revenue.toLocaleString('fr-FR')} €. Acceptez avant J${deadlineDay}.`,
            day: today,
            read: false,
          });
        });
      });

      // 2. Expire calls past their deadline (not yet accepted = available).
      const survivingAvail = [];
      const expiredCalls = [];
      avail.forEach(c => {
        if (c.spotCall && Number.isFinite(c.spotCallDeadlineDay) && today > c.spotCallDeadlineDay) {
          expiredCalls.push(c);
        } else {
          survivingAvail.push(c);
        }
      });
      avail = survivingAvail;

      expiredCalls.forEach(c => {
        const eng = updatedEngagements.find(e => e.id === c.spotCallEngagementId);
        if (!eng || !eng.active) return;
        eng.strikes = (eng.strikes || 0) + 1;
        eng.missedCalls = (eng.missedCalls || 0) + 1;
        reputationDelta -= 3;
        cashDelta -= 800; // contractual fine
        messages.push({
          type: 'spot-miss',
          eng,
          contract: c,
        });
        if (eng.strikes >= 3) {
          eng.active = false;
          toEnd.push(eng.id);
        }
      });

      // 3. Close engagements past their endsOnDay normally.
      updatedEngagements.forEach(eng => {
        if (eng.active && today >= eng.endsOnDay) {
          eng.active = false;
          messages.push({ type: 'spot-end', eng });
        }
      });

      // Apply state changes.
      set({
        spotEngagements: updatedEngagements,
        availableContracts: avail,
        treasury: (state.treasury || 0) + cashDelta,
        reputation: Math.max(0, Math.min(100, (state.reputation || 0) + reputationDelta)),
        // 10/02/2026 (Lot J Bug 2) — Pousse les notifs `spot_call_incoming`
        // dans le centre de notifications pour que l'apparition d'un nouvel
        // appel ponctuel soit IMPOSSIBLE à louper.
        ...(newCallNotifications.length > 0
          ? { notifications: [...(state.notifications || []), ...newCallNotifications].slice(-40) }
          : {}),
      });

      // Fire inbox messages (best-effort).
      const push = get().pushCustomMessage;
      if (typeof push === 'function') {
        // 10/02/2026 (Lot J Bug 2) — Pousse les inbox alerts pour les NEW
        // spot calls (chaque appel ponctuel arrivé aujourd'hui).
        newCallMessages.forEach(payload => {
          try { push(payload); }
          catch (err) { console.warn('[spotEngagement] new call inbox failed:', err); }
        });
        messages.forEach(m => {
          try {
            if (m.type === 'spot-miss') {
              push({
                senderType: 'client',
                senderName: m.eng.clientName,
                tone: 'warning',
                kind: 'warning',
                subject: `Mission manquée — pénalité ${m.eng.clientName}`,
                body:
                  `Bonjour,\n\nNous regrettons que vous n'ayez pas pu honorer notre demande « ${m.contract.name} » dans le délai contractuel.\n\n` +
                  `**Conséquences immédiates :**\n` +
                  `• Pénalité : -800 € (prélevée ce jour)\n` +
                  `• Réputation : -3 points\n` +
                  `• Strike : ${m.eng.strikes}/3\n\n` +
                  (m.eng.strikes >= 3
                    ? `⚠ **Vous avez atteint 3 manquements. Notre engagement d'exclusivité ponctuelle est RÉSILIÉ avec effet immédiat.**\n\n`
                    : `⚠ ${3 - m.eng.strikes} manquement(s) avant résiliation automatique.\n\n`) +
                  `Cordialement,\nL'équipe ${m.eng.clientName}`,
                ttlDays: 14,
              });
            } else if (m.type === 'spot-end') {
              push({
                senderType: 'client',
                senderName: m.eng.clientName,
                tone: 'positive',
                kind: 'info',
                subject: `Engagement exclusif terminé — ${m.eng.clientName}`,
                body:
                  `Bonjour,\n\nLa période d'engagement de 90 jours arrive à son terme.\n\n` +
                  `📊 **Bilan :**\n` +
                  `• Missions honorées : ${m.eng.completedCalls}\n` +
                  `• Missions manquées : ${m.eng.missedCalls}\n\n` +
                  `Nous avons été ${m.eng.missedCalls === 0 ? 'pleinement satisfaits' : 'globalement satisfaits'} de votre prestation. ` +
                  `Un nouvel appel d'offres sera lancé prochainement — votre dossier sera examiné en priorité.\n\nCordialement,\nL'équipe ${m.eng.clientName}`,
                ttlDays: 14,
              });
            }
          } catch (err) { console.warn('[spotEngagement] notif failed:', err); }
        });
      }

      if (toEnd.length > 0 && reputationDelta < 0) {
        // Extra reputation hit on termination.
        const extra = -10 * toEnd.length;
        set({ reputation: Math.max(0, Math.min(100, (get().reputation || 0) + extra)) });
      }
    },

    /**
     * Called from acceptContract when the player picks up a spot call.
     * Increments completedCalls on the engagement.
     */
    onSpotCallAccepted: (contractInstanceId) => {
      const state = get();
      const engagements = state.spotEngagements || [];
      // contractInstanceId is the original `spot-call-…-XXX`, but the
      // contract object on activeContracts keeps `spotCallEngagementId`.
      const ac = (state.activeContracts || []).find(c => c.instanceId === contractInstanceId);
      const engId = ac?.spotCallEngagementId;
      if (!engId) return;
      const updated = engagements.map(eng => eng.id === engId
        ? { ...eng, completedCalls: (eng.completedCalls || 0) + 1 }
        : eng);
      set({ spotEngagements: updated });
    },

    /**
     * Selector used by Tenders UI to filter spot vs classical tenders.
     */
    getActiveSpotEngagements: () => (get().spotEngagements || []).filter(e => e.active),
  };
}
